/**
 * AppRouter - Main router configuration
 *
 * Layout Strategy:
 * - Public routes: Marketing pages, auth pages
 * - /inbox: Uses InboxLayout (renders IconRail internally, no AppShell sidebar)
 * - /sales, /templates, /analytics, /integrations, /settings: Use AppShell (sidebar + header + content)
 */

import { ComponentType, Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { loadTranslations } from "../contexts/LanguageContext";
import { getSessionPayload, hasAccessToken, isAdminLikeRole, isSuperUserRole } from "../lib/authSession";
import { IS_ONPREM } from "../lib/deployment";

/**
 * Every screen is its own chunk. Marketing pages are prerendered and hydrated:
 * entry-server.tsx renders the same Suspense boundary, so React keeps the
 * server HTML on screen until the page's chunk (modulepreloaded by
 * scripts/prerender.mjs) arrives. The app, auth and booking pages also wait
 * for the UI strings they use. Each page has its own Suspense so the AppShell
 * stays put while the next screen loads.
 */
type LoadPage = () => Promise<{ default: ComponentType<any> }>;

function lazyPage(load: LoadPage, withStrings: boolean) {
  const preload = () => (withStrings ? Promise.all([load(), loadTranslations()]).then(([m]) => m) : load());
  const Lazy = lazy(preload);
  const Page = (props: Record<string, unknown>) => (
    <Suspense fallback={null}>
      <Lazy {...props} />
    </Suspense>
  );
  return Object.assign(Page, { preload });
}

const marketingPage = (load: LoadPage) => lazyPage(load, false);
const page = (load: LoadPage) => lazyPage(load, true);

const LandingPage = marketingPage(() => import("../pages/LandingPage"));
const FeaturesPage = marketingPage(() => import("../pages/FeaturesPage"));
const PricingPage = marketingPage(() => import("../pages/PricingPage"));
const SectorMarinaPage = marketingPage(() => import("../pages/SectorMarinaPage"));
const SectorSalonPage = marketingPage(() => import("../pages/SectorSalonPage"));
const AppointmentsLandingPage = marketingPage(() => import("../pages/AppointmentsLandingPage"));
const SecurityPage = marketingPage(() => import("../pages/SecurityPage"));
const BlogPage = marketingPage(() => import("../pages/BlogPage"));
const AboutPage = marketingPage(() => import("../pages/AboutPage"));
const CareersPage = marketingPage(() => import("../pages/CareersPage"));
const HelpCenterPage = marketingPage(() => import("../pages/HelpCenterPage"));
const SalesDocPage = marketingPage(() => import("../pages/SalesDocPage"));
const PrivacyPolicyPage = marketingPage(() => import("../pages/PrivacyPolicyPage"));
const TermsOfServicePage = marketingPage(() => import("../pages/TermsOfServicePage"));

const LoginPage = page(() => import("../pages/LoginPage"));
const SandboxLoginPage = page(() => import("../pages/SandboxLoginPage"));
const SignUpPage = page(() => import("../pages/SignUpPage"));
const ForgotPasswordPage = page(() => import("../pages/ForgotPasswordPage"));
const ResetPasswordPage = page(() => import("../pages/ResetPasswordPage"));
const OnboardingPage = page(() => import("../pages/OnboardingPage"));
const InboxPage = page(() => import("../pages/InboxPage"));
const TemplatesPage = page(() => import("../pages/TemplatesPage"));
const AnalyticsPage = page(() => import("../pages/AnalyticsPage"));
const IntegrationsPage = page(() => import("../pages/IntegrationsPage"));
const SalesDashboardPage = page(() => import("../pages/SalesDashboardPage"));
const SettingsPage = page(() => import("../pages/SettingsPage"));
const VoiceSettingsPage = page(() => import("../pages/VoiceSettingsPage"));
const KnowledgeBasePage = page(() => import("../pages/KnowledgeBasePage"));
const SocialReportPage = page(() => import("../pages/SocialReportPage"));
const SocialComposerPage = page(() => import("../pages/SocialComposerPage"));
const RequestsPage = page(() => import("../pages/RequestsPage"));
const FieldPage = page(() => import("../pages/FieldPage"));
const PlanPage = page(() => import("../pages/PlanPage"));
const AudioImportPage = page(() => import("../pages/AudioImportPage"));
const CsatDashboardPage = page(() => import("../pages/CsatDashboardPage"));
const ROIDashboardPage = page(() => import("../pages/ROIDashboardPage"));
const WidgetPage = page(() => import("../pages/WidgetPage"));
const InternalAiLabPage = page(() => import("../pages/InternalAiLabPage"));
const AiReplyHealthPage = page(() => import("../pages/AiReplyHealthPage"));
const NotificationsPage = page(() => import("../pages/NotificationsPage").then((m) => ({ default: m.NotificationsPage })));
const OAuthCallbackPage = page(() => import("../pages/OAuthCallbackPage").then((m) => ({ default: m.OAuthCallbackPage })));
const AppShell = page(() => import("../components/AppShell"));
const OnboardingGuard = page(() => import("../components/OnboardingGuard").then((m) => ({ default: m.OnboardingGuard })));
const DebugMetaPage = page(() => import("../pages/DebugMetaPage"));
const BookingsPage = page(() => import("../pages/BookingsPage"));
const CustomersPage = page(() => import("../pages/CustomersPage"));
const PublicBookingPage = page(() => import("../pages/PublicBookingPage"));
const BookingManagePage = page(() => import("../pages/BookingManagePage"));

/** Internal tools (AI Lab, Meta debug) are for platform staff, not customers. */
const StaffOnly = ({ children }: { children: JSX.Element }) =>
  isSuperUserRole(getSessionPayload()?.role) ? children : <Navigate to="/inbox" replace />;

/** Workspace admins see their own organisation; staff can also see all. */
const AdminOnly = ({ children }: { children: JSX.Element }) =>
  isAdminLikeRole(getSessionPayload()?.role) ? children : <Navigate to="/inbox" replace />;

const AppRouter = () => {
  const authed = hasAccessToken();
  if (authed) {
    // The guard, the shell and the screen are nested lazy chunks; fetch them
    // side by side instead of one after another.
    void OnboardingGuard.preload();
    void AppShell.preload();
  }

  return (
    <Routes>
      {/* Public marketing routes */}
      {/* On-prem has no marketing site and no sign-up: the door is the login page. */}
      <Route
        path="/"
        element={authed ? <Navigate to="/onboarding" replace /> : IS_ONPREM ? <Navigate to="/login" replace /> : <LandingPage />}
      />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      {/* Vertical sector pages. Salons are the first sector we sell to (#75). */}
      <Route path="/sectors/kuafor" element={<SectorSalonPage />} />
      <Route path="/sectors/marina" element={<SectorMarinaPage />} />
      <Route path="/randevu" element={<AppointmentsLandingPage />} />
      <Route path="/guvenlik" element={<SecurityPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/help-center" element={<HelpCenterPage />} />

      {/* Legal routes — public.
          Both short (/privacy) and long (/privacy-policy) variants exist
          because the footer + cookie banner link to the long form, but
          existing email templates use the short form. Keep both. */}
      <Route path="/pp" element={<PrivacyPolicyPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      {/* Sales documents the payment institution reviews (content/salesTerms.ts). */}
      <Route path="/distance-sales-agreement" element={<SalesDocPage doc="distance-sales" />} />
      <Route path="/pre-information-form" element={<SalesDocPage doc="pre-information" />} />
      <Route path="/refund-policy" element={<SalesDocPage doc="refund" />} />

      {/* Customer-facing reservation pages — no auth, no AppShell.
          /book/:slug is what a business sends to a customer; /booking/:token
          is the cancel link at the bottom of every confirmation mail. Both
          are opened by people who have never logged in, so they must resolve
          before the authed block and before the wildcard redirect. */}
      <Route path="/book/:slug" element={<PublicBookingPage />} />
      <Route path="/booking/:token" element={<BookingManagePage />} />

      {/* Auth routes */}
      <Route path="/login" element={authed ? <Navigate to="/onboarding" replace /> : <LoginPage />} />
      <Route path="/sandbox" element={authed ? <Navigate to="/inbox" replace /> : <SandboxLoginPage />} />
      <Route
        path="/signup"
        element={authed ? <Navigate to="/onboarding" replace /> : IS_ONPREM ? <Navigate to="/login" replace /> : <SignUpPage />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* OAuth callback routes - handles redirects from OAuth providers.
          - /oauth/callback: legacy / generic landing after backend has finished
            the exchange and redirected back with ?oauth_success=... .
          - /oauth/:provider/callback: the URL we register with Meta/Google/etc.
            Meta sends the user here with ?code=...&state=... — the page then
            forwards those params to the backend so it can exchange the code
            for a token. Without this route, the SPA falls through to the
            wildcard and the OAuth code is dropped on the floor. */}
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      <Route path="/oauth/:provider/callback" element={<OAuthCallbackPage />} />

      {/* Keep onboarding route always registered to avoid falling back to landing */}
      <Route
        path="/onboarding"
        element={
          authed ? (
            <OnboardingGuard>
              <ErrorBoundary>
                <OnboardingPage />
              </ErrorBoundary>
            </OnboardingGuard>
          ) : (
            <Navigate to="/signup?next=/onboarding" replace />
          )
        }
      />

      {authed && (
        <>
          {/* Authenticated routes use AppShell as layout wrapper */}
          <Route
            element={
              <OnboardingGuard>
                <AppShell />
              </OnboardingGuard>
            }
          >
            {/* /inbox: InboxLayout renders its own IconRail (no AppShell sidebar) */}
            <Route
              path="/inbox"
              element={
                <ErrorBoundary>
                  <InboxPage />
                </ErrorBoundary>
              }
            />

            {/* Other routes: Use AppShell sidebar + header + content layout */}
            <Route
              path="/sales"
              element={
                <ErrorBoundary>
                  <SalesDashboardPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/templates"
              element={
                <ErrorBoundary>
                  <TemplatesPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/analytics"
              element={
                <ErrorBoundary>
                  <AnalyticsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/integrations"
              element={
                <ErrorBoundary>
                  <IntegrationsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/widget"
              element={
                <ErrorBoundary>
                  <WidgetPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/settings"
              element={
                <ErrorBoundary>
                  <SettingsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/settings/voice"
              element={
                <ErrorBoundary>
                  <VoiceSettingsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/bookings"
              element={
                <ErrorBoundary>
                  <BookingsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/customers"
              element={
                <ErrorBoundary>
                  <CustomersPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/social"
              element={
                <ErrorBoundary>
                  <SocialReportPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/social/compose"
              element={
                <ErrorBoundary>
                  <SocialComposerPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/field"
              element={
                <ErrorBoundary>
                  <FieldPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/requests"
              element={
                <ErrorBoundary>
                  <RequestsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/knowledge"
              element={
                <ErrorBoundary>
                  <KnowledgeBasePage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/plans"
              element={
                // On-prem the plan is the contract; there is nothing to buy.
                IS_ONPREM ? (
                  <Navigate to="/settings" replace />
                ) : (
                  <ErrorBoundary>
                    <PlanPage />
                  </ErrorBoundary>
                )
              }
            />
            <Route
              path="/audio"
              element={
                <ErrorBoundary>
                  <AudioImportPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/csat"
              element={
                <ErrorBoundary>
                  <CsatDashboardPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/roi"
              element={
                <ErrorBoundary>
                  <ROIDashboardPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/notifications"
              element={
                <ErrorBoundary>
                  <NotificationsPage />
                </ErrorBoundary>
              }
            />
            <Route
              path="/ai-lab"
              element={
                <StaffOnly>
                  <ErrorBoundary>
                    <InternalAiLabPage />
                  </ErrorBoundary>
                </StaffOnly>
              }
            />
            <Route
              path="/ai-health"
              element={
                <AdminOnly>
                  <ErrorBoundary>
                    <AiReplyHealthPage />
                  </ErrorBoundary>
                </AdminOnly>
              }
            />
            <Route
              path="/debug-meta"
              element={
                <StaffOnly>
                  <ErrorBoundary>
                    <DebugMetaPage />
                  </ErrorBoundary>
                </StaffOnly>
              }
            />
          </Route>
        </>
      )}

      {/* Default redirect */}
      <Route path="*" element={<Navigate to={authed ? "/inbox" : "/"} replace />} />
    </Routes>
  );
};

export default AppRouter;
